/**
 * Conflict resolution dialog (AC-ARCH-005, FR-CONFLICT-04).
 *
 * Two ways to settle a disagreement, exactly as the acceptance criteria state:
 *   1. **pick a submission** — a radio per competing version, labelled with who
 *      sent it and when, so the choice is made on provenance rather than on a
 *      UUID;
 *   2. **hand-craft one** — a JSON payload seeded from the active version, for
 *      the case where neither operator got it entirely right.
 *
 * A post-close resolution is a Team Admin override of a closed record, so the
 * service demands a written reason of at least ten characters
 * (`requireEditReason`). The dialog enforces the same floor client-side and
 * still renders whatever the server says if the two ever disagree — the server
 * is the authority, the client is the courtesy.
 */
import { useMemo, useState } from 'react';

import { OverlayDialog } from '../../components/overlay-dialog';
import type { PlayVersionPage, ResolveConflictInput } from '../../lib/api-types';
import { EVENT_REASON_MIN_LENGTH } from '../../lib/constants';

import { formatTimestamp, roleLabel } from './versions-panel';

type ResolutionMode = 'existing' | 'custom';

export interface ResolveDialogProps {
  playNumber: number;
  versions: PlayVersionPage;
  /** Human-readable failure from the last attempt. Never a stack trace. */
  error?: string | null;
  onSubmit: (input: ResolveConflictInput) => Promise<void>;
  onCancel: () => void;
}

/** Parse the custom-payload editor. Returns the reason it is unusable, if any. */
export function parseCustomPayload(raw: string): {
  payload: Record<string, unknown> | null;
  error: string | null;
} {
  if (raw.trim() === '') return { payload: null, error: 'Enter the stat values to record.' };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { payload: null, error: 'This is not valid JSON. Check for a missing comma or quote.' };
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { payload: null, error: 'The custom resolution must be a JSON object of stat fields.' };
  }
  return { payload: parsed as Record<string, unknown>, error: null };
}

export function ResolveDialog({
  playNumber,
  versions,
  error = null,
  onSubmit,
  onCancel,
}: ResolveDialogProps): JSX.Element {
  const isPostClose = versions.event_status === 'closed';
  const activePayload = useMemo(
    () =>
      versions.items.find((version) => version.is_active)?.stat_payload ??
      versions.items[0]?.stat_payload ??
      {},
    [versions.items]
  );

  const [mode, setMode] = useState<ResolutionMode>('existing');
  const [selectedVersionId, setSelectedVersionId] = useState<string>('');
  const [customJson, setCustomJson] = useState(() => JSON.stringify(activePayload, null, 2));
  const [note, setNote] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const trimmedNote = note.trim();
  const isNoteValid = !isPostClose || trimmedNote.length >= EVENT_REASON_MIN_LENGTH;
  const isSelectionMade = mode === 'custom' || selectedVersionId !== '';
  const canSubmit = isSelectionMade && isNoteValid && !isBusy;

  const handleSubmit = async (): Promise<void> => {
    setValidationError(null);
    if (mode === 'existing' && selectedVersionId === '') {
      setValidationError('Select a version or provide a custom resolution.');
      return;
    }
    const input: ResolveConflictInput = {};
    if (mode === 'existing') {
      input.selected_version_id = selectedVersionId;
    } else {
      const { payload, error: parseError } = parseCustomPayload(customJson);
      if (payload === null) {
        setValidationError(parseError);
        return;
      }
      input.custom_stat_payload = payload;
    }
    if (trimmedNote !== '') input.note = trimmedNote;

    setIsBusy(true);
    try {
      await onSubmit(input);
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <OverlayDialog
      title={`Resolve play #${playNumber}`}
      onClose={isBusy ? () => undefined : onCancel}
    >
      <div className="resolve-dialog">
        <p className="resolve-dialog__consequence">
          The version you pick becomes the play of record for every coach and every stat page. The
          losing submissions stay on file for audit.
        </p>

        <fieldset className="resolve-dialog__group">
          <legend>Use an existing submission</legend>
          {versions.items.map((version) => (
            <label key={version.version_id} className="resolve-option">
              <input
                type="radio"
                name="resolution"
                value={version.version_id}
                checked={mode === 'existing' && selectedVersionId === version.version_id}
                onChange={() => {
                  setMode('existing');
                  setSelectedVersionId(version.version_id);
                }}
              />
              <span className="resolve-option__text">
                Submitted by {roleLabel(version.submitted_by_role)} at{' '}
                {formatTimestamp(version.submitted_at)}
                {version.is_active ? ' (currently active)' : ''}
              </span>
            </label>
          ))}
        </fieldset>

        <fieldset className="resolve-dialog__group">
          <legend>Or hand-craft the result</legend>
          <label className="resolve-option">
            <input
              type="radio"
              name="resolution"
              value="custom"
              checked={mode === 'custom'}
              onChange={() => setMode('custom')}
            />
            <span className="resolve-option__text">Custom resolution</span>
          </label>
          {mode === 'custom' ? (
            <div className="field">
              <label className="field__label" htmlFor="resolve-custom-payload">
                Stat payload (JSON)
              </label>
              <textarea
                id="resolve-custom-payload"
                className="field__control resolve-dialog__editor"
                rows={10}
                value={customJson}
                onChange={(event) => setCustomJson(event.target.value)}
              />
              <p className="field__hint">
                Seeded from the active version. The server re-validates every field.
              </p>
            </div>
          ) : null}
        </fieldset>

        <div className={`field${!isNoteValid && trimmedNote !== '' ? ' field--invalid' : ''}`}>
          <label className="field__label" htmlFor="resolve-note">
            {isPostClose ? 'Reason for correction (required)' : 'Resolution note (optional)'}
          </label>
          <textarea
            id="resolve-note"
            className="field__control"
            rows={3}
            value={note}
            aria-invalid={!isNoteValid && trimmedNote !== '' ? true : undefined}
            onChange={(event) => setNote(event.target.value)}
          />
          <p className="field__hint">
            {isPostClose
              ? `This event is closed — the correction is audited and needs at least ${EVENT_REASON_MIN_LENGTH} characters.`
              : 'Filed with the resolution for anyone reviewing the game later.'}
          </p>
          {isPostClose && trimmedNote !== '' && !isNoteValid ? (
            <p className="field__error" role="alert">
              Give at least {EVENT_REASON_MIN_LENGTH} characters explaining the correction.
            </p>
          ) : null}
        </div>

        {validationError !== null || error !== null ? (
          <div className="banner banner--error" role="alert">
            <p>{validationError ?? error}</p>
          </div>
        ) : null}

        <div className="btn-row">
          <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={isBusy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
          >
            {isBusy ? 'Resolving…' : 'Resolve conflict'}
          </button>
        </div>
      </div>
    </OverlayDialog>
  );
}
